import {buildEntrySchemaFor} from '@github-ui/swp-core/schemas/contentful/entry'
import {LinkSchema} from '../components/link'
import {AssetSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/asset'
import * as z from 'zod'
import {RichTextSchema} from '@github-ui/swp-core/schemas/contentful/richText'
import {safeOptional} from '@github-ui/swp-core/schemas/safe-optional'
import {AssetItemSchema} from '../components/assetItem'
import {IconLinkItemSchema} from '../components/iconLinkItem'
import {ImageLinkItemSchema} from '../components/imageLinkItem'
import {IconItemSchema} from '../components/iconItem'
import {TextItemSchema} from '../components/textItem'
import {IconAssetLinkItemSchema} from '../components/iconAssetLinkItem'
import {LabelAssetLinkItemSchema} from '../components/labelAssetLinkItem'
import {MediaSchema} from '../components/media'
import {OverviewTemplateSettingsSchema} from '../components/overviewTemplateSettings'
import {PricingOptionSchema} from '../components/pricingOption'
import {CustomerStorySchema, RiverBreakoutSchema} from '../components/riverBreakout'
import {SubnavSchema} from '../components/subnav'
import {FaqGroupSchema} from '../components/faqItem'

export const HeroVariantEnum = z.enum(['Stacked', 'Carousel', 'Icon Cards', 'Image Cards'])
export const SupportDetailsVariantEnum = z.enum(['Pillars', 'Icon Cards', 'Image Cards', 'Statistics'])
export const FeatureDetailsEnum = z.enum(['Accordion', 'Stack'])
export const FeatureDetailsTwoEnum = z.enum(['Breakout', 'Carousel'])
export const FeatureDetailsThreeEnum = z.enum(['Footsteps', 'Large Cards'])
export const IntroContextEnum = z.enum(['Simple Intro', 'Pillars', 'Icon Cards', 'Image Cards', 'Stats'])
export const FeatureDetailsTestimonialEnum = z.enum(['Quote', 'Statistics', 'Brand Banner'])

export type HeroVariant = z.infer<typeof HeroVariantEnum>
export type IntroContext = z.infer<typeof IntroContextEnum>

export const TemplateFlexSuiteOverviewSchema = buildEntrySchemaFor('templateFlexSuiteOverview', {
  fields: z.lazy(() =>
    z.object({
      title: z.string().optional(),
      // Template Settings (e.g. branded experience)
      settings: safeOptional(OverviewTemplateSettingsSchema),
      // Hero
      heroHeading: RichTextSchema,
      heroLabel: z.string().optional(),
      heroDescription: safeOptional(RichTextSchema),
      heroCtaOne: LinkSchema,
      heroCtaTwo: safeOptional(LinkSchema),
      heroTertiaryDescription: safeOptional(RichTextSchema),
      // Variant fields
      heroVariant: safeOptional(HeroVariantEnum),
      heroAsset: safeOptional(MediaSchema),
      heroCarousel: safeOptional(z.array(AssetItemSchema)),
      heroIconCards: safeOptional(z.array(IconItemSchema)),
      heroImageCards: safeOptional(z.array(ImageLinkItemSchema)),
      // SubNav
      subNav: safeOptional(SubnavSchema),
      // Featured Content
      featureHeading: safeOptional(RichTextSchema),
      featureLabel: z.string().optional(),
      featureDescription: safeOptional(RichTextSchema),
      featureLink: safeOptional(LinkSchema),
      featureDetailsOne: safeOptional(FeatureDetailsEnum),
      featureRiverAccordion: safeOptional(z.array(IconAssetLinkItemSchema)),
      featureRiverStack: safeOptional(z.array(LabelAssetLinkItemSchema)),
      featureDetailsTwo: safeOptional(FeatureDetailsTwoEnum),
      featureRiverBreakout: safeOptional(RiverBreakoutSchema),
      featureBreakoutTabs: safeOptional(z.array(IconAssetLinkItemSchema)),
      featureDetailsThree: safeOptional(FeatureDetailsThreeEnum),
      featureDetailsThreeHeading: safeOptional(RichTextSchema),
      featureFootstepsLink: safeOptional(LinkSchema),
      featureFootstepsLabel: z.string().optional(),
      featureFootstepsCards: safeOptional(z.array(ImageLinkItemSchema)),
      featureLargeCards: safeOptional(z.array(ImageLinkItemSchema)),
      featureDetailsTestimonial: safeOptional(FeatureDetailsTestimonialEnum),
      featureTestimonialLink: safeOptional(LinkSchema),
      featureTestimonialQuote: safeOptional(RichTextSchema),
      featureTestimonialLabel: z.string().optional(),
      featureTestimonialLogo: z.string().optional(),
      featureTestimonialStats: safeOptional(z.array(TextItemSchema)),
      featureTestimonialImage: safeOptional(AssetSchema),
      featureTestimonialName: z.string().optional(),
      featureTestimonialTitle: z.string().optional(),
      // Intro Section
      introLogoHeading: z.string().optional(),
      introLogos: safeOptional(z.array(z.string())),
      introLogoLink: safeOptional(LinkSchema),
      introContext: safeOptional(IntroContextEnum),
      introContextHeading: safeOptional(RichTextSchema),
      introContextLink: safeOptional(LinkSchema),
      introContextBody: safeOptional(RichTextSchema),
      introItems: safeOptional(z.array(z.union([IconLinkItemSchema, TextItemSchema]))),
      introIconCards: safeOptional(z.array(IconLinkItemSchema)),
      introPillars: safeOptional(z.array(IconItemSchema)),
      introImageCards: safeOptional(z.array(ImageLinkItemSchema)),
      introStats: safeOptional(z.array(TextItemSchema)),
      // Support Section
      supportHeading: safeOptional(RichTextSchema),
      supportLabel: z.string().optional(),
      supportDescription: safeOptional(RichTextSchema),
      supportDetails: safeOptional(SupportDetailsVariantEnum),
      supportPillars: safeOptional(z.array(IconItemSchema)),
      supportIconCards: safeOptional(z.array(IconLinkItemSchema)),
      supportImageCards: safeOptional(z.array(ImageLinkItemSchema)),
      supportStats: safeOptional(z.array(TextItemSchema)),
      // Pricing
      includePricingSection: safeOptional(z.boolean()),
      pricingHeading: safeOptional(RichTextSchema),
      pricingLabel: z.string().optional(),
      pricingDescription: safeOptional(RichTextSchema),
      pricingOptions: safeOptional(z.array(PricingOptionSchema)),
      pricingCtas: safeOptional(z.array(LinkSchema)),
      pricingTiles: safeOptional(z.array(z.string())),
      pricingTilesLead: safeOptional(RichTextSchema),
      //Outro Section
      outroCustomerStories: safeOptional(z.array(CustomerStorySchema)),
      outroCtaBanner: safeOptional(z.boolean()),
      outroCtaHeading: safeOptional(RichTextSchema),
      outroCtaDescription: safeOptional(RichTextSchema),
      outroCtaOne: safeOptional(LinkSchema),
      outroCtaTwo: safeOptional(LinkSchema),
      outroIncludeResources: safeOptional(z.boolean()),
      outroResourcesHeading: safeOptional(RichTextSchema),
      outroResources: safeOptional(z.array(IconLinkItemSchema)),
      outroHeading: safeOptional(RichTextSchema),
      outroFaqs: safeOptional(z.array(FaqGroupSchema)),
    }),
  ),
})

export type TemplateFlexSuiteOverview = z.infer<typeof TemplateFlexSuiteOverviewSchema>
